import type { PropsWithChildren, FunctionComponent } from 'react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Form,
  FormSection,
  FormTitle,
  Input,
  Modal,
  ModalBody,
  Text,
  View,
} from './ui/index';
import type { ModalProps } from './ui/Modal/Modal';
import { ModalFooter } from './ui/Modal/Modal';
import type { CommitStatus } from './Scenario';
import { ScenarioCommitPolicy } from './Scenario';
import type {
  MasterSchedule,
  UserSchedule,
} from '@seaber/common/core/scenario';
import { getMergedScenario } from './MergeScenarios';
import { assertDefined } from '../lib/util';
import { throttle } from 'lodash';
import { InfoIcon } from './Icons';
import {
  buildCommit,
  generateCommitSummary,
  isAutoSyncEnabledForScenario,
  isEmptyCommit,
} from './ScenarioUtil';
import { useApolloClient } from '@apollo/client';
import type { EventTimeline } from './core/Timelines';
import type { CommitInput } from '../graphql';
import { useLastCommitQuery } from '../graphql';
import { useLocalSetting } from './hooks/useLocalConfig';
import { useIsUpdateAvailable } from './hooks/useVersionCheck';
import { dateStringToNumOpt } from './core/util';
import { toast } from 'react-toastify';
import type {
  GetScenarioData,
  CommittedPlanningData,
} from '../lib/planningData';
import { useCurrentUser } from '../state/zustand';
import * as Sentry from '@sentry/browser';
import { useVoyageNamingRules } from './hooks/useVoyageNamingRules';
import type { Entry } from '@seaber/common/core/entries';

const InfoBox: FunctionComponent<PropsWithChildren<{ marginTop?: string }>> = (
  props,
) => (
  <Box
    style={{
      display: 'flex',
      minHeight: '50px',
      minWidth: '400px',
      marginRight: '8px',
      marginBottom: '12px',
      marginTop: props.marginTop,
      padding: '12px',
      alignItems: 'center',
      flex: '0 0 auto',
      borderStyle: 'dashed',
      borderWidth: '2px',
      borderColor: 'rgba(0, 0, 0, 0.41)',
    }}
  >
    <Box style={{ marginRight: '1em', alignSelf: 'center' }}>
      <InfoIcon />
    </Box>
    {props.children}
  </Box>
);

export type CommitScenarioModalProps = Partial<ModalProps> & {
  onCommit?: (
    statusUpdate: (status: CommitStatus) => void,
    opts: { syncWithMaster: boolean; suppressEmail: boolean },
  ) => Promise<unknown>;
  scenarioToCommit?: UserSchedule;
  masterScenario?: MasterSchedule;
  timelines?: EventTimeline[];
  committedData?: CommittedPlanningData;
  getScenarioData?: GetScenarioData;
};

const commitTableToUserFriendly: Record<string, string> = {
  scheduled_order: 'scheduled orders',
  handled_by_coa: 'COA assignments',
  handled_by_spot: 'spot assignments',
  off_hire: 'off-hires',
  port_call: 'port calls',
  single_event: 'single events',
  cargo_compartment_allocation: 'cargo allocations',
  orders: 'orders',
};

const CommitScenarioModal = ({
  onCommit,
  scenarioToCommit,
  masterScenario,
  onClose,
  timelines,
  committedData,
  getScenarioData,
  ...restProps
}: CommitScenarioModalProps): JSX.Element | null => {
  assertDefined(scenarioToCommit, `Requires a scenario to commit`);
  assertDefined(onCommit, `Commit modal callbacks are required`);
  assertDefined(timelines, `Commit modal requires timelines data`);
  assertDefined(
    getScenarioData,
    `Commit modal requires getScenarioData method`,
  );

  const commitStatus = useRef<CommitStatus>({
    currentActionName: 'Committing',
  });
  const [commitError, setCommitError] = useState<string | null>(null);
  const [commitStarted, setCommitStarted] = useState(false);
  const statusTextRef = useRef<HTMLSpanElement | null>(null);
  const commitStatusText = useRef<string>('');

  const currentUser = useCurrentUser();
  const currentUserId = currentUser?.id ?? null;
  const currentOrgId = currentUser?.organizationId ?? null;
  const isAutoSyncEnabled = isAutoSyncEnabledForScenario(
    !!currentUser?.rootOrganization?.configAutoSyncUserScenarios,
    scenarioToCommit,
  );

  const [commitMethod, setCommitMethod] = useLocalSetting(
    'commitPolicy',
    'merge',
  );

  const policy =
    commitMethod === 'replace'
      ? ScenarioCommitPolicy.OVERRIDE
      : ScenarioCommitPolicy.MERGE_COMPATIBLE;

  const isUpdateAvailable = useIsUpdateAvailable();

  const { data: lastCommitData, loading: lastCommittedDataLoading } =
    useLastCommitQuery({
      fetchPolicy: 'no-cache',
    });

  const lastCommitAt = dateStringToNumOpt(
    lastCommitData?.lastCommit?.nodes[0]?.committedAt,
  );

  /** Has latest master data been updated according to the newest commit on server */
  const isMasterUpToDate =
    lastCommittedDataLoading || lastCommitAt === masterScenario?.masterCommitAt;

  /** Is the current scenario aware of the latest commit to master */
  const isScenarioBasedOnLatest =
    !lastCommitAt || lastCommitAt === scenarioToCommit.masterCommitAt;

  const isIntegration = !!scenarioToCommit.isIntegrationMaster;

  /** Is the latest master data merged to the current scenario */
  const isScenarioSynced =
    // Integration master scenario is always the best source of truth, so we ignore merges with committed state
    isIntegration ||
    // Otherwise check the sync timestamp
    (scenarioToCommit.syncedWithMasterAt ?? 0) >= (lastCommitAt ?? 0);

  const requiresSyncWithMaster =
    !isIntegration &&
    (!isScenarioBasedOnLatest ||
      (!isScenarioSynced &&
        policy === ScenarioCommitPolicy.MERGE_COMPATIBLE && // Always require sync if merging
        isAutoSyncEnabled)); // When auto sync enabled the scenario should have been synced already, if not there has been some problem

  const requiresReload = !isMasterUpToDate;

  const [initialLastCommitAt] = useState(lastCommitAt);
  const requiresCheckTimelines =
    initialLastCommitAt !== null && initialLastCommitAt !== lastCommitAt;

  // Updating commit status handled with refs and direct mutation of the element because we don't want to make commit any longer with extra re-renders of the page
  const updateStatusText = () => {
    const status = commitStatus.current;
    commitStatusText.current = status.currentActionName
      ? `${status.currentActionName}
        ${
          status.currentCompleted !== undefined &&
          status.currentTotal !== undefined
            ? ` ${status.currentCompleted}/${status.currentTotal}`
            : ''
        }`
      : 'Done';
    if (statusTextRef.current) {
      statusTextRef.current.innerText = commitStatusText.current;
    }
  };

  const voyageNamingRuleByShipId = useVoyageNamingRules(currentOrgId);

  const throttledUpdateStatus = throttle(
    (newStatus) => {
      commitStatus.current = newStatus;
      updateStatusText();
    },
    100,
    {
      leading: true,
      trailing: true,
    },
  );

  const commit = useCallback(
    (opts: { syncWithMaster: boolean; suppressEmail: boolean }) => {
      if (commitStarted) {
        throw new Error(`Already committing`);
      }

      setCommitStarted(true);
      setCommitError(null);

      return onCommit(throttledUpdateStatus, opts)
        .then(() => onClose?.())
        .catch((err) => {
          console.error('Commit failed', err);
          const originalMessage: string = err.message || '';
          let errorMessage: string;
          if (originalMessage.includes('policy for table')) {
            const tableName = /"([\w_]+)"/.exec(originalMessage)?.[1];
            if (tableName && commitTableToUserFriendly[tableName]) {
              errorMessage = `Commit failed. You don't have sufficient permissions to update all ${commitTableToUserFriendly[tableName]}.`;
            } else {
              errorMessage = `Commit failed. You don't have sufficient permissions to update all relevant data.`;
            }
          } else if (
            originalMessage.includes(
              'could not obtain lock on row in relation "commit_log"',
            )
          ) {
            errorMessage = `Commit failed. Someone else is committing at the same time. Try again in a moment.`;
          } else {
            errorMessage = `Commit failed. ${originalMessage}`;
          }
          toast.error(errorMessage);
          setCommitError(errorMessage);
          setCommitStarted(false);
        });
    },
    [commitStarted, onClose, onCommit, throttledUpdateStatus],
  );

  const [suppressEmail, setSuppressEmail] = useState<boolean>(false);

  const [commitSummaryError, setCommitSummaryError] = useState<null | string>(
    null,
  );
  const [commitInput, setCommitInput] = useState<null | CommitInput>(null);
  const [commitDelta, setCommitDelta] = useState<
    null | [Entry, Partial<Entry>][]
  >(null);
  const commitSummary = useMemo(() => {
    return commitInput && generateCommitSummary(commitInput);
  }, [commitInput]);

  const commitHasChanges = useMemo(
    () => !!commitInput && !isEmptyCommit(commitInput),
    [commitInput],
  );

  const client = useApolloClient();

  const syncedUserScenario: UserSchedule | null = useMemo(
    () =>
      (scenarioToCommit &&
        masterScenario &&
        (policy === ScenarioCommitPolicy.OVERRIDE
          ? scenarioToCommit
          : getMergedScenario(scenarioToCommit, masterScenario, {
              sourceIsCommit: true,
            }))) ||
      null,
    [policy, scenarioToCommit, masterScenario],
  );

  const syncedScenarioDerivatives = useMemo(
    () => syncedUserScenario && getScenarioData(syncedUserScenario),
    [syncedUserScenario, getScenarioData],
  );

  useEffect(() => {
    setCommitInput(null);
    setCommitDelta(null);
    setCommitSummaryError(null);

    if (
      currentUser &&
      committedData &&
      syncedUserScenario &&
      syncedScenarioDerivatives &&
      commitStatusText.current !== 'Done'
    ) {
      buildCommit(
        client,
        syncedUserScenario,
        syncedScenarioDerivatives.timelines,
        voyageNamingRuleByShipId,
        {
          policy,
          currentUser,
          committedData,
          noSideEffects: true,
          suppressEmail: true,
          getScenarioData,
        },
      )
        .then((buildResult) => {
          setCommitInput(buildResult.commit);
          setCommitDelta(buildResult.deltas);
          setCommitSummaryError(null);
        })
        .catch((err) => {
          console.error(`Could not create commit object`, err);
          setCommitSummaryError(err.message);
        });
    }
  }, [
    syncedUserScenario,
    client,
    timelines,
    commitMethod,
    currentUser,
    committedData,
    policy,
    syncedScenarioDerivatives,
    voyageNamingRuleByShipId,
    getScenarioData,
  ]);

  const cannotCommitMessage = isUpdateAvailable
    ? 'New application version is available. Reload the page before committing.'
    : requiresCheckTimelines
      ? 'Committed scenario was updated on the server. Check updated timelines before committing.'
      : requiresReload
        ? 'Committed scenario has updated on the server. Reload the page before committing.'
        : requiresSyncWithMaster
          ? 'Your local scenario is out of sync with master. Try reloading the page or manually sync with committed version .'
          : null;

  const [showDetailedCommit, setShowDetailedCommit] = useState(false);

  const transactionForReadyToCommit =
    React.useRef<ReturnType<typeof Sentry.startTransaction>>();
  React.useEffect(() => {
    if (!currentUserId || !currentOrgId) {
      return;
    }
    const tx = Sentry.startTransaction({ name: 'Ready to commit' });
    if (!tx) {
      console.warn(`Sentry reporting is not initialized!`);
      return;
    }
    transactionForReadyToCommit.current = tx;
  }, [currentOrgId, currentUserId]);

  useEffect(() => {
    const isReadyToCommit = commitInput && commitSummary && !commitSummaryError;
    if (!isReadyToCommit) {
      return;
    }
    if (!transactionForReadyToCommit.current) {
      return;
    }
    transactionForReadyToCommit.current.finish();
    transactionForReadyToCommit.current = undefined;
  }, [commitInput, commitSummary, commitSummaryError]);

  return (
    <Modal
      isOpen
      onClose={() => onClose?.()}
      {...restProps}
      height="auto"
      width="50em"
    >
      <ModalBody p={0} flexBasis="content" minHeight="30em" maxHeight="80vh">
        <Form borderStyle="none">
          <FormSection>
            <FormTitle data-testid="commit-scenario-modal">
              <Text fontSize="1.4em" fontWeight="bold">
                Commit scenario
              </Text>
            </FormTitle>
          </FormSection>
          <FormSection borderBottomStyle="none">
            {commitInput && commitSummary && !cannotCommitMessage && (
              <>
                <Text fontSize="1.2em" fontWeight="bold" marginBottom={2}>
                  Changes to commit:
                </Text>
                {!commitHasChanges && <Text fontSize="1.2em">No changes.</Text>}
                {!isScenarioSynced && (
                  <Text
                    fontSize="0.8em"
                    fontWeight=""
                    marginBottom={1}
                    color="orange.orange_600"
                  >
                    Note: Scenario has not been synced with master since last
                    commit
                  </Text>
                )}{' '}
                {commitHasChanges && commitSummary.length === 0 && (
                  <Text fontSize="1.2em">
                    There are some technical changes that could not be easily
                    summarized. You should still commit.
                  </Text>
                )}
                {commitSummary.map(([label, count]) => (
                  <Text fontSize="1.2em" key={label}>
                    {label}: {count}
                  </Text>
                ))}
                {!commitError && (
                  <InfoBox marginTop="1em">
                    <Box ml={3} display="flex" flexDirection="row" flexGrow={1}>
                      <Text fontSize="1.2em">
                        <span ref={statusTextRef}>
                          {commitStatusText.current || 'Ready to commit'}
                        </span>
                      </Text>
                    </Box>
                  </InfoBox>
                )}
                {(process.env.APP_ENV === 'development' ||
                  window.location.search.includes('dev')) && (
                  <label>
                    <Box display="flex">
                      <Input
                        size={16}
                        type="checkbox"
                        checked={showDetailedCommit}
                        onChange={() => {
                          setShowDetailedCommit(!showDetailedCommit);
                        }}
                      />
                      <Text alignSelf="center">Show detailed commit info</Text>
                    </Box>
                  </label>
                )}
                {showDetailedCommit && (
                  <Box
                    ml={3}
                    display="flex"
                    flexDirection="column"
                    flexGrow={1}
                  >
                    <Text fontSize="1.2em">
                      <pre>
                        {JSON.stringify(
                          commitDelta?.map(([entry, delta]) => [
                            entry.category,
                            entry.dbId,
                            delta,
                          ]),
                          null,
                          2,
                        )}
                      </pre>
                    </Text>
                    <Text fontSize="1.2em">
                      <pre>{JSON.stringify(commitInput, null, 2)}</pre>
                    </Text>
                  </Box>
                )}
              </>
            )}
            {!commitSummary &&
              !commitSummaryError &&
              !commitError &&
              !cannotCommitMessage && (
                <>
                  <Box
                    ml={3}
                    display="flex"
                    flexDirection="column"
                    flexGrow={1}
                  >
                    <Text fontSize="1.2em">
                      <span>
                        Calculating the summary of changes to commit...
                      </span>
                    </Text>
                    <Text fontSize="1.2em">
                      <span ref={statusTextRef}>
                        {commitStatusText.current}
                      </span>
                    </Text>
                  </Box>
                </>
              )}
            {cannotCommitMessage && (
              <InfoBox marginTop="1em">
                <Box ml={3} display="flex" flexDirection="row" flexGrow={1}>
                  <Text fontSize="1.2em">
                    <span>{cannotCommitMessage}</span>
                  </Text>
                </Box>
              </InfoBox>
            )}

            {commitSummaryError ||
              (commitError && (
                <>
                  <InfoBox marginTop="1em">
                    <Box ml={3} display="flex" flexDirection="row" flexGrow={1}>
                      <Text fontSize="1.2em" color="red">
                        <span>
                          {commitError ||
                            'Error calculating commit data, cannot commit'}
                        </span>
                      </Text>
                    </Box>
                  </InfoBox>
                </>
              ))}
          </FormSection>
        </Form>
      </ModalBody>
      <ModalFooter>
        <View display="flex" flexDirection="column" width="100%">
          {!cannotCommitMessage && (
            <>
              <View display="flex" flexDirection="row" paddingBottom={3}>
                <label>
                  <Input
                    type="radio"
                    size={16}
                    checked={commitMethod === 'replace'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCommitMethod('replace');
                      }
                    }}
                    disabled={commitStarted}
                  />
                  <Text color="white">Replace committed</Text>
                </label>
                <label style={{ marginLeft: '1em', marginRight: '1em' }}>
                  <Input
                    type="radio"
                    size={16}
                    checked={commitMethod === 'merge'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setCommitMethod('merge');
                      }
                    }}
                    disabled={commitStarted}
                  />
                  <Text color="white">Merge to committed (recommended)</Text>
                </label>
                <Box flexGrow={1} />
                <label>
                  <Input
                    type="checkbox"
                    size={16}
                    checked={suppressEmail}
                    onChange={(e) => {
                      setSuppressEmail(e.target.checked);
                    }}
                    disabled={commitStarted}
                  />
                  <Text color="white">Do NOT send email notifications</Text>
                </label>
              </View>
              <hr />
            </>
          )}

          <View display="flex" flexDirection="row" paddingTop={3}>
            <Button mr={3} onPress={() => onClose?.()}>
              Cancel
            </Button>
            <Box flexGrow={1} />
            {!cannotCommitMessage && (
              <Button
                aria-label="Do commit"
                variant={commitMethod === 'merge' ? 'secondary' : 'negative'}
                mr="auto"
                disabled={commitStarted || !commitSummary}
                onPress={async () => {
                  const tx = Sentry.startTransaction({
                    name: 'Committing scenario',
                    description: commitMethod,
                  });

                  await commit({
                    syncWithMaster: commitMethod === 'merge',
                    suppressEmail,
                  });

                  tx?.finish();
                }}
              >
                {commitMethod === 'merge'
                  ? 'Merge & commit'
                  : 'Replace committed scenario'}
              </Button>
            )}
            {requiresReload && (
              <Button
                aria-label="Reload"
                variant={'secondary'}
                mr="auto"
                onPress={() => window.location.reload()}
              >
                Reload now
              </Button>
            )}
          </View>
        </View>
      </ModalFooter>
    </Modal>
  );
};

export default CommitScenarioModal;
